(ns app.front.view.sign-off
  "Application sign-off view. The view consists of two dynamic sections (Parties and Project
  Information) that get their data from the backend. The rest of the sections are the regular form
  data of the configured sign-off form."
  (:require [app.domain.application.model :as app-model]
            [app.domain.business.model :as business-model]
            [app.domain.form.model :as form-model]
            [app.domain.form.render-link :as form-link]
            [app.domain.form.schema :as form-schema]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.party.model :as party-model]
            [app.domain.sign-off.declarations :as sign-off-declarations]
            [app.domain.sign-off.legacy-declarations :as legacy-declarations]
            [app.domain.sign-off.model :as sign-off-model]
            [app.domain.sign-off.sign-off-properties :as sign-off-properties]
            [app.domain.workspace.model :as workspace-model]
            [app.front.acceptance.components :as acceptance]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.file.upload :as upload]
            [app.front.form.render :as fr]
            [app.front.form.subs :as form-subs]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.error-list :as ui-error-list]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.typography :refer [p]]
            [app.front.util :refer [for-all for-frag for-frag-indexed scroll-to-bottom scroll-to-top tval]]
            [app.front.view.application.progress :as app-progress]
            [app.front.view.form :as form-view]
            [app.front.view.form-util :as form-util]
            [app.front.view.not-found :as not-found]
            [app.front.view.party.common :as party-common]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.domain.system-of-units :as sou]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

(rc/reg-chain ::application-data
  (fn [{db :db}]
    {:db (dissoc db ::application-data)})

  (fn [_ [_ workspace-id]]
    {:comm/command {:command :sign-off/application-data
                    :method  :get
                    :data    {:workspace-id workspace-id}}})

  (fn [{db :db} [_ _ _ resp]]
    {:db       (assoc db ::application-data resp)
     :dispatch [::form-subs/navigate-form {:form-id (:sign-off-form-id resp)}]}))


(rf/reg-sub ::application-data
  :-> ::application-data)

(rf/reg-sub ::parties
  :<- [::application-data]
  :-> :parties)


(rf/reg-sub ::processed-parties
  :<- [::ws-subs/party-roles]
  :<- [::parties]
  (fn [[party-roles parties] _]
    (sign-off-model/process-parties party-roles parties)))

(rf/reg-sub ::applicants
  :<- [::processed-parties]
  (fn [parties _]
    (filter #(contains? (:role-keys %) :role/applicant) parties)))


(rf/reg-sub ::party-members-of-role
  :<- [::processed-parties]
  (fn [parties [_ role]]
    (filter #(contains? (:role-keys %) (:party-role/key role))
            parties)))


(rf/reg-sub ::my-parties-of-role
  (fn [[_ role]]
    (rf/subscribe [::party-members-of-role role]))
  (fn [parties _]
    (filter :my-party? parties)))


(rf/reg-sub ::information
  :<- [::application-data]
  :-> :information)

(rf/reg-sub ::properties
  :<- [::application-data]
  :-> :properties)

(rf/reg-sub ::principal-applicant
  :<- [::applicants]
  :<- [:session/user]
  (fn [[applicants current-user] _]
    (or (p/find-first applicants {:full-name (user-model/full-name current-user)})
        (first applicants))))


(rf/reg-sub ::principal-for-role
  (fn [db [_ role my-parties-only?]]
    (or (get-in db [::principal-party (:db/id role)])
        (cond->> (<sub [::party-members-of-role role])
          my-parties-only? (filter :my-party?)
          (not my-parties-only?) (sort-by :my-party? >)
          :always first))))


(rf/reg-event-db ::set-principal-for-role
  (fn [db [_ party-role id]]
    (assoc-in db [::principal-party (:db/id party-role)] id)))


(rf/reg-event-fx ::sign-off-completed
  (fn [_ _]
    (scroll-to-bottom)
    nil))


(defn- from-wizard? [db]
  (-> db :route/match :parameters :query :wizard))

(defn- current-workspace-id [db]
  (-> db :route/match :parameters :path :workspace-id))

;; When sign-off was reached from the new application wizard (see
;; app.front.view.application-wizard.navigation/redirect-to-sign-off), navigate back into the wizard
;; instead of the plain workspace view once sign-off is complete.
(rf/reg-event-fx ::back-to-workspace
  (fn [{:keys [db]} _]
    (let [workspace-id (current-workspace-id db)]
      {:route/push (if (from-wizard? db)
                     {:route  :view/application-wizard-end
                      :params {:workspace-id workspace-id}}
                     {:route  :view/workspace
                      :params {:workspace-id workspace-id}})})))

(rf/reg-event-fx ::continue-to-payment
  (fn [{:keys [db]} _]
    (let [workspace-id (current-workspace-id db)]
      {:route/push (if (from-wizard? db)
                     {:route  :view/application-wizard-workspace
                      :params {:workspace-id workspace-id
                               :step         "payment"}}
                     {:route  :view/workspace
                      :params {:workspace-id workspace-id}})})))


(rf/reg-sub ::sign-off-pdf-file
  :<- [::ws-subs/application]
  :<- [::ws-subs/required-so-roles]
  (fn [[application required-roles]]
    (or (when (= (count required-roles) 1)
          ;; When there is just a single signature provided via file upload, there is no separate
          ;; sign-off-form PDF generated, so you can just download the uploaded form. Due to a bug
          ;; that existed until 7/2022 file entities for sign-off-form were generated even when the
          ;; actual PDF was never produced. So we try to find the uploaded file in these situations.
          (<sub [::entities/entity {:entity/parent (:db/id application)
                                    :file/category :file.category/sign-off-upload}]))
        (<sub [::entities/entity {:entity/parent (:db/id application)
                                  :file/category :file.category/sign-off-form}]))))


(rf/reg-event-db ::show-needs-changes-modal
  (fn [db _]
    (assoc db ::needs-changes-modal {:open? true
                                     :text  ""})))


(rf/reg-event-db ::hide-needs-changes-modal
  (fn [db _]
    (dissoc db ::needs-changes-modal)))


(rf/reg-event-db ::set-needs-changes-text
  (fn [db [_ text]]
    (assoc-in db [::needs-changes-modal :text] text)))


(rf/reg-sub ::needs-changes-modal
  :-> ::needs-changes-modal)


(rf/reg-sub ::from-wizard?
  :<- [:route/match]
  (fn [match _]
    (boolean (-> match :parameters :query :wizard))))

(rf/reg-sub ::back-href
  :<- [::ws-subs/href]
  :<- [::ws-subs/workspace-id]
  :<- [::from-wizard?]
  (fn [[ws-href workspace-id from-wizard?] _]
    (if from-wizard?
      (href/href :view/application-wizard-workspace
                 {:workspace-id workspace-id
                  :step         "summary"})
      ws-href)))


(rf/reg-sub ::application-id
  :<- [::ws-subs/workspace]
  (fn [workspace]
    (-> workspace workspace-model/workspace-application :db/id)))




(rf/reg-sub ::primary-action
  :<- [::application-id]
  (fn [application-id]
    (let [submit-status     @(rf/subscribe [::app-progress/signal-allowed-status :process.signal/submit])
          pre-submit-status @(rf/subscribe [::app-progress/signal-allowed-status :process.signal/to-pre-submit-payment])
          make-action       (fn [signal-id status on-success]
                              (when (contains? #{:allowed :disabled} status)
                                {:tr-key    signal-id
                                 :disabled? (not= status :allowed)
                                 :evt       [:app.front.process/signal
                                             application-id
                                             [signal-id]
                                             {:on-success on-success}]}))]
      (or (make-action :process.signal/submit submit-status [::back-to-workspace])
          (make-action :process.signal/to-pre-submit-payment pre-submit-status [::continue-to-payment])))))


(rf/reg-sub ::options
  :<- [::ws-subs/tr]
  :<- [::application-id]
  :<- [::sign-off-pdf-file]
  :<- [::process/app-signal-permitted? [:process.signal/sign-off-needs-changes {}]]
  (fn [[tr application-id sign-off-pdf needs-changes-permitted?]]
    {:items (cond-> []
              needs-changes-permitted?
              (conj {:id       "sign-off-needs-changes"
                     :label    (tr [:sign-off/needs-changes-button-text])
                     :on-click #(>evt [::show-needs-changes-modal application-id])})
              sign-off-pdf
              (conj {:id    "download-sign-off-pdf"
                     :label (tr [:sign-off/download])
                     :href  (file-download/download-href :file/download-workspace-file
                                                         {"file-id"  (:db/id sign-off-pdf)
                                                          "download" "true"})}))}))


(rf/reg-sub ::view-data
  :<- [::back-href]
  :<- [::primary-action]
  :<- [::options]
  (fn [[back-href primary-action options]]
    {:back-href      back-href
     :primary-action primary-action
     :options        options}))


(defn header [{:keys [tr disabled? form form-id] :as ctx}]
  (let [latest-acceptance (<sub [:app.front.acceptance.subs/entity-latest-acceptance form-id])
        has-no-fields?    (->> form
                               form-model/get-form-data-schema
                               form-schema/get-fields-from-schema
                               empty?)]
    [:div.page-header.mb-4
     [:div.mt-4.bx--row.flex-1.justify-between.items-center
      [:div.flex.items-center
       (when-not (or disabled? has-no-fields?)
         [:div.mr-8
          [form-view/autosave-info ctx]])]]
     (when (not-empty latest-acceptance)
       [:div.bx--row.mt-4.mb-4
        [:div.bx--col-xs-12
         [acceptance/render-acceptance tr :entity.type/form latest-acceptance]]])
     [:h1.mt-4 (tr [:view/sign-off])]]))


(defn- data-items-to-display-from-props
  ([tag]
   (data-items-to-display-from-props tag nil))
  ([tag properties]
   (-> [tag {:properties properties}]
       sign-off-properties/resolve-properties-for-element
       flatten)))


(def default-class-for-data-items
  {:class-name "bx--col-small-12 bx--col-md-6 bx--col-xl-4 bx--col-xxl-3"})


(defmethod fr/render :sign-off/parties [{:keys [tr] ::keys [parties]} _key-idx & _]
  (when-let [parties (seq parties)]
    (let [lang (<sub [:language])]
      [section/section
       "sign-off-form-parties"
       {:tr         tr
        :class-name "mt-12"}
       (tr [:sign-off/parties])
       [:div.mt-4.bx--row
        (for-frag [{:keys [sign-off-roles full-name street-address postal-code
                           municipality-province]                              :party/keys [business]} parties]
          [fr/column-labeled-text
           (merge default-class-for-data-items
                  {:text-class-name  "whitespace-pre-line text-sm text-gray-700 leading-tight"
                   :label-class-name "font-semibold"})
           (if (<sub [::ws-subs/loading-config?])
             (cc/skeleton-text)
             (->> sign-off-roles
                  (map party-common/party-role-label)
                  (str/join ", ")))
           (->> [(if business
                   (business-model/format-name business lang)
                   full-name)
                 (some-> business :business/type vector tr)
                 (some->> business :business/id (str (tr [:business/id]) " "))
                 street-address
                 municipality-province
                 postal-code]
                (remove str/blank?)
                (str/join "\n"))])]])))


(defn- add-unit-for-data-item [{:keys [unit unit-label]}]
  (cond
    unit-label
    (str " " unit-label)

    unit
    (str " " (get-in sou/units [unit :label]))

    :else
    nil))


(defn- round-number-value [{:keys [value unit]}]
  (if unit
    (sou/round sou/default-precision value)
    value))


(defn- resolve-value-for-data-item [tr information k {:keys [value rich-text?] :as data-item}]
  (cond
    (#{:application-type :planning.application-type} k)
    (->> [(-> information :category-name :value)
          (-> information :work-type-name :value)
          (-> information :work-target-name :value)]
         (remove nil?)
         (str/join " / "))

    (= :workspace/municipality k)
    (municipality-model/common-name-province value tr (<sub [:language]))

    (inst? value)
    ((<sub [::date-util/date-fn]) value)

    (number? value)
    (str (round-number-value data-item)
         (add-unit-for-data-item data-item))

    (boolean? value)
    (-> value str keyword vector tr)

    (or (nil? value)
        (and (string? value) (str/blank? value)))
    (when data-item "-")

    rich-text?
    [ui-rt/editor-legacy {:id            (name k)
                          :initial-value value}]

    (string? value)
    (str value (add-unit-for-data-item data-item))

    :else
    nil))


(defn information->data-rows [tr information properties]
  (into [:div.mt-4.bx--row]
        (for-all [{ref :ref label-from-prop :label} properties
                  :let [data-item (get information ref)
                        value     (resolve-value-for-data-item tr information ref data-item)]
                  :when value]
          (let [label (or label-from-prop (fr/label-text-silent data-item) (tr [ref]))]
            (fr/column-labeled-text default-class-for-data-items
                                    label
                                    value)))))


(defn general-component-to-display-info
  ([tr information tag-name]
   (general-component-to-display-info tr information tag-name nil))
  ([tr information tag-name properties]
   (if-let [info (not-empty information)]
     (information->data-rows tr info (data-items-to-display-from-props tag-name properties))
     (cc/skeleton-text {:paragraph true}))))


(defmethod fr/render :sign-off/project-information [{:keys [tr] ::keys [information]} _ _ [_ {:keys [properties]}]]
  (general-component-to-display-info tr information :sign-off/project-information properties))

(defmethod fr/render :sign-off/purpose-of-application [{:keys [tr] ::keys [information]} _ _ [_ {:keys [properties]}]]
  (general-component-to-display-info tr information :sign-off/purpose-of-application properties))

(defmethod fr/render :sign-off.planning/purpose-of-application [{:keys [tr] ::keys [information]} & _]
  (general-component-to-display-info tr information :sign-off.planning/purpose-of-application))

(defmethod fr/render :sign-off.land-use/purpose-of-application [{:keys [tr] ::keys [information]} & _]
  (general-component-to-display-info tr information :sign-off.land-use/purpose-of-application))

(defmethod fr/render :sign-off.planning/description-of-subject-property [{:keys [tr] ::keys [information]} & _]
  (general-component-to-display-info tr information :sign-off.planning/description-of-subject-property))

(defmethod fr/render :sign-off.land-use/description-of-subject-property [{:keys [tr] ::keys [information]} & _]
  (general-component-to-display-info tr information :sign-off.land-use/description-of-subject-property))

(defmethod fr/render :sign-off.planning/application-forms [{:keys [tr] ::keys [information]} & _]
  (general-component-to-display-info tr information :sign-off.planning/application-forms))


(defmethod fr/render :sign-off/application-information [_ & _]
  ; No front-end implementation
  [:div])


(defmethod fr/render :sign-off/declaration [{:keys [tr]} & _]
  (when-let [applicant (<sub [::principal-applicant])]
    [:p.mt-4 {}
     (tr [:sign-off/declaration]
         [(-> applicant :full-name)])]))


(def h2 :h2.uppercase.mb-2.mt-8.text-sm.leading-tight.font-semibold)


(defmethod fr/render :sign-off.rezoning/application-information [_ & _]
  ; No front-end implementation
  [:div])


(defmethod fr/render :sign-off.rezoning/declaration-section [_ & _]
  ; No front-end implementation
  [:div])


(defmethod fr/render :sign-off.rezoning/pre-consultation-information [{:keys [tr-schema] ::keys [information]} _ _ [_ opts]]
  (when-let [info (not-empty information)]
    (let [format-date    (<sub [::date-util/date-fn])
          completed-date (some-> info :pre-consultation-completed :value format-date)
          workspace-id   (-> info :workspace/id :value)
          tr             (tr-schema opts)]
      [:div
       [:p (tr [:sign-off.rezoning/pre-consultation-info])]
       [:p [:strong (if completed-date
                      (tr [:sign-off.rezoning/pre-consultation-completed] [completed-date workspace-id])
                      (tr [:sign-off.rezoning/pre-consultation-not-completed] [workspace-id]))]]])))



(defmethod fr/render :sign-off/subject-land-information [{:keys [tr] ::keys [properties]} & _]
  (for-frag [{:keys [:property/id :property/legal primary address]} properties]
    (into [:div.mt-4.bx--row {:key (str "subject-land-" id)}]
          (->> [[(tr [:workspace/address]) (str address (when primary (str " (" (tr [:property/primary]) ")")))]
                [(tr [:property/id]) id]
                [(tr [:property/legal]) legal]]
               (mapv (fn [[label value]]
                       (fr/column-labeled-text default-class-for-data-items
                                               label
                                               (resolve-value-for-data-item tr
                                                                            nil
                                                                            nil
                                                                            {:value value}))))))))


(defn signature [{:keys [tr signer created]}]
  [:div.flex.items-center
   (cc/icon {:icon        i/undersign
             :fill        "green"
             :description (tr [:signature/signed-by])})
   [:div.ml-2
    [:p.p-0.leading-tight
     (tr [:signature/signed-by])
     [:span.font-bold.mx-1 {:data-cy "sign-off-signed-by"}
      (sign-off-model/format-signee-name signer tr)]
     ((<sub [::date-util/date-time-fn]) created)]]])


(defn uploaded [{:keys [tr]} {:sign-off/keys [file signed-for] :as sig}]
  (when file
    (let [uploaded-by (user-model/full-name sig)]
      [:div.flex.items-center
       (cc/icon {:icon        i/undersign
                 :fill        "green"
                 :description (tr [:signature/signed-by])})
       [:p.ml-2.p-0
        [:span (tr [:signature/uploaded-by])]
        [:span.font-bold.mx-1 uploaded-by]
        (when-not (= uploaded-by signed-for)
          [:<>
           (tr [:signature/authorization-letter-from])
           [:span.font-bold.mr-1 (sign-off-model/format-signee-name sig tr)]])
        ((<sub [::date-util/date-time-fn]) (:entity/created sig))]])))


(defn uploaded-file-link [{:keys [tr application-id role]} file]
  [:div.flex.items-start.md:items-center.justify-between.flex-col.md:flex-row
   [:div.flex.items-center
    (cc/icon
      {:icon        i/document
              ; This icon seems to include about 1px white on the left side
       :style       {:margin-left "-1px"}
       :description (tr [:sign-off/pdf-title])})
    (cc/link
      {:class-name "ml-2 mr-8"
       :href       (form-util/download-url application-id (:db/id role))
       :target     "_blank"}
      (:file/filename file))]])


(defn- signer-select [{:keys [lang tr party-role available-parties selected-party disabled?]}]
  [ui-select/select {:id           (str "sign-off-role-select-" (:db/id party-role))
                     :class-name   "full-width-select"
                     :label-text   (tr [:sign-off/signer])
                     :allow-empty? false
                     :disabled     disabled?
                     :value        selected-party
                     :values       available-parties
                     :val->str     #(if-let [business (:party/business %)]
                                      (business-model/format-name business lang)
                                      (:full-name %))
                     :on-change    #(>evt [::set-principal-for-role party-role %])}])


(defn visible-sign-off-signal-errors [signal-errors missing-role-error?]
  (cond->> signal-errors
    missing-role-error? (remove (comp #{:process.guard/sign-off-role-requirement-met?} first))
    :always seq))


(defn sign-off-button
  [{:keys [tr application-id role disabled? other-sign-offs-done?]}]
  (r/with-let [*show-loading (r/atom false)]
    (let [lang               (<sub [:language])
          role-id            (:db/id role)
          user-parties       (<sub [::my-parties-of-role role])
          principal-for-role (<sub [::principal-for-role role true])
          signal             [:process.signal/application-sign-off
                              {:role-id  role-id
                               :party-id (:db/id principal-for-role)}]
          missing-role-error (when-not principal-for-role
                               (tr [:sign-off/sign-off-has-to-be-done-by-the-role]
                                   [(party-common/party-role-label role)]))
          signal-errors      (<sub [::process/signal-errors-for-error-list application-id signal])
          signal-permitted?  (<sub [::process/signal-permitted? application-id signal])
          visible-errors     (visible-sign-off-signal-errors signal-errors (some? missing-role-error))]
      (if @*show-loading
        (cc/inline-loading {:description (tr [:sign-off/processing])
                            :success     (false? (<sub [:app.front.process/signal-processing?
                                                        :process.signal/application-sign-off]))
                            :on-success  (fn [_]
                                           (reset! *show-loading false)
                                           true)})
        [:div.flex.flex-col
         [:div.flex.flex-row.flex-wrap.items-end.w-full.gap-4
          (when (> (count user-parties) 1)
            [signer-select (util/mmap {:available-parties user-parties
                                       :selected-party    principal-for-role
                                       :party-role        role
                                       :disabled?         disabled?}
                                      lang tr)])
          [:div
           (cc/button
             {:kind             :primary
              :size             :field
              :on-click         (fn [_]
                                  (reset! *show-loading true)
                                  (>evt [:app.front.process/signal
                                         application-id
                                         signal
                                         (when other-sign-offs-done?
                                           {:on-success [::sign-off-completed]})]))
              :data-cy          "sign-off-signature"
              :icon             i/undersign
              :disabled         (or disabled?
                                    (not signal-permitted?))
              :title            (cond
                                  (seq visible-errors)
                                  (or missing-role-error
                                      (->> visible-errors
                                           (map (comp tr vector first))
                                           (str/join ", ")))
                                  :else
                                  "")
              :icon-description ""}
             (tr [:signature/sign-off]))]]
         (when missing-role-error
           [:div.mt-4
            [:div.flex.items-center
             (cc/icon {:icon        i/circle-attention
                       :description (tr [:warning/warning])
                       :fill        "#616161"
                       :class-name  "mr-2 w-6 flex-shrink-0"})
             [:div.leading-tight.flex-shrink
              missing-role-error]]])
         (when (seq visible-errors)
           [:div.mt-4
            [ui-error-list/signal-error-list tr visible-errors]])]))))


(defn manual-sign-off
  "If a given role has signed off via upload, render the uploaded file.
  If a given role has signed off with a click, don't render anything.
  If a given role has not signed off yet, render the upload sign off."
  [{:keys [application-id form disabled? role tr show-download-form? authorization-letter?]
    :as   ctx}]
  (let [lang               (<sub [:language])
        role-key           (:party-role/key role)
        sign-off           (sign-off-model/sign-off-entity-for-role form role)
        sign-off-file      (:sign-off/file sign-off)
        principal-for-role (<sub [::principal-for-role role])
        parties            (<sub [::party-members-of-role role])]
    [:<>
     (when (and sign-off (some? sign-off-file))
       [uploaded-file-link ctx sign-off-file])
     (when-not sign-off
       [:div.flex.mb-2
        [:div.self-center
         (if (= 1 (count parties))
           [:<>
            [p "text-xs mb-1" (tr [:sign-off/signer])]
            [p (-> parties first :full-name)]]
           [signer-select (util/mmap {:available-parties parties
                                      :selected-party    principal-for-role
                                      :party-role        role
                                      :disabled?         disabled?}
                                     lang tr)])]
        (when show-download-form?
          [:div.ml-8.mr-4.self-end
           (cc/button
             {:icon             i/download
              :icon-description (tr [:sign-off/download])
              :disabled         disabled?
              :href             (form-util/download-url application-id
                                                        (:db/id role)
                                                        (:db/id principal-for-role)
                                                        true
                                                        true)
              :kind             :tertiary}
             (tr [:sign-off/download]))])
        [upload/upload-button
         (str "sof-upload-" (some-> role-key name))
         {:attach-file-fn  (fn [file-id filename content-type _]
                             (>evt [:app.front.process/signal
                                    application-id
                                    [:process.signal/sign-off-upload
                                     {:file-id      file-id
                                      :filename     filename
                                      :content-type content-type
                                      :role-id      (:db/id role)
                                      :signing-for  (:db/id principal-for-role)}]]))
          :button-i18n-key (if authorization-letter?
                             :signature/upload-auth-letter
                             :signature/upload)
          :disabled?       disabled?
          :accept-types    [".pdf" "application/pdf"]
          :class           "ml-4 self-end"}]])]))


(defn remove-sign-off [{:keys [tr application-id] {role-id :db/id} :role}]
  (when (<sub [:app.front.process/app-signal-permitted? [:process.signal/cancel-sign-off {:role-id role-id}]])
    [:div.relative.ml-2 {:style {:top "1px"}}
     [ui/modal-confirmation-button
      {:tr                  tr
       :id                  "remove-signature"
       :on-yes-click        #(>evt [:app.front.process/signal application-id
                                    [:process.signal/cancel-sign-off {:role-id role-id}]])
       :text-button?        true
       :trigger-button-text (tr [:signature/remove])}
      [:div.flex.flex-col
       [:p.bx--modal-content__text.mb-6
        (tr [:signature/remove-confirmation])]]]]))


(defn sign-off-controls
  "Manual sign off should be used in cases where upload-only? = true or
  role has active parties but none of them have user linked to them"
  [{:keys [upload-only? form required-roles tr] :as ctx} role current-id]
  (let [role-key                        (:party-role/key role)
        application                     (<sub [::ws-subs/application])
        my-parties                      (<sub [::my-parties-of-role role])
        role-only-has-non-user-parties? (party-model/role-only-has-non-user-parties? application role-key)
        sign-off                        (sign-off-model/sign-off-entity-for-role form role)
        field-id                        (sign-off-model/role->field-id role-key)
        ;; field id doesn't exist if sign-off created with old source code
        field                           (fr/child-of current-id field-id ctx)
        method                          (:sign-off/method sign-off)
        upload-permitted?               (<sub [::process/app-signal-permitted?
                                               [:process.signal/sign-off-upload {:role-id (:db/id role)}]])
        read-only-sign-off?             (and (not sign-off)
                                             (empty? my-parties)
                                             (not upload-only?)
                                             (not role-only-has-non-user-parties?))
        show-sign-off-button?           (or read-only-sign-off?
                                            (and (seq my-parties)
                                                 (not upload-only?)
                                                 (not role-only-has-non-user-parties?)))
        show-manual-sign-off?           (or (and (not read-only-sign-off?)
                                                 (not show-sign-off-button?))
                                            (-> sign-off
                                                :sign-off/method
                                                (= :sign-off.method/upload)))
        other-sign-offs-done?           (sign-off-model/signed-for-role-keys? form
                                                                              (disj (set required-roles) role-key))
        any-signals-processing?         (boolean (seq (<sub [:app.front.process/current-processing-signals])))
        ctx                             (merge ctx
                                               {:disabled? (or (:disabled? ctx) any-signals-processing?)}
                                               (util/mmap other-sign-offs-done?
                                                          role
                                                          role-only-has-non-user-parties?))]
    [:div.sign-off-controls.flex.flex-col
     [:div.flex.items-center.mb-2
      (case method
        :sign-off.method/click [signature (util/mmap {:signer sign-off :created (:entity/created sign-off)} tr)]
        :sign-off.method/upload [uploaded ctx sign-off]
        (when show-sign-off-button?
          [sign-off-button (assoc ctx
                                  :disabled? (or (:disabled? ctx) read-only-sign-off?)
                                  :form-field-id (:db/id field))]))
      (when sign-off
        [remove-sign-off ctx])]
     (when show-manual-sign-off?
       [manual-sign-off (assoc ctx :disabled? (or (:disabled? ctx)
                                                  (not upload-permitted?)))])]))


(defmethod fr/render :sign-off/declaration-section-v2
  ;; Renderer for declaration schema in entity.type/declaration-config entities
  [{:keys [role] :as ctx} key-idx current-id [_ _ & children]]
  [:div
   [:h3.mb-2.mt-6.text-sm.uppercase.leading-tight.font-semibold
    (party-common/party-role-label role)]
   (for-frag-indexed [[i child] children]
     [fr/render-form-element ctx (str key-idx "-" i) current-id child])])


(defmethod fr/render :sign-off/declaration-subsection
  [ctx key-idx current-id [_ {:keys [label]} & children]]
  [:div.mt-2.mb-2
   [:h4.mb-3.text-sm.leading-tight.font-semibold label]
   (for-frag-indexed [[i child] (sign-off-declarations/process-placeholders ctx children)]
     (if (string? child)
       [:p.pb-3 (form-link/text-with-anchors child)]
       [fr/render-form-element ctx (str key-idx "-" i) current-id child]))])


(defn sign-off
  ;; New version of the sign-off where the declaration text is not stored in the form schema
  [{:keys [tr form upload-only? preview?] ::keys [permit-type get-principal] :as ctx} required-role current-id]
  (let [declaration-schema  (<sub [::ws-subs/sign-off-declaration-schema-for-role required-role])
        required-role-key   (:party-role/key required-role)
        required-role-label (party-common/party-role-label required-role)]
    (if-let [party (get-principal required-role)]
      [:div.mb-10.last:mb-0
       (cond
         declaration-schema
         [fr/render-form-element
          (assoc ctx :role required-role :party party)
          0
          current-id
          declaration-schema]

         preview?
         [legacy-declarations/editor-preview-schema tr required-role-key]

         :else
         ((legacy-declarations/role->declaration permit-type
                                                 required-role-key
                                                 (-> ctx
                                                     :form
                                                     :form/schema
                                                     :form.schema/id
                                                     form-model/get-form-name-from-id))
          (:full-name party)
          tr))
       (when (and (not (sign-off-model/sign-off-entity-for-role form required-role))
                  (not upload-only?)
                  (party-model/role-only-has-non-user-parties? (<sub [::ws-subs/application]) required-role-key))
         [:div.mt-6.bold (tr [:sign-off/only-upload-is-available] [required-role-label])])
       [:div.mt-6
        [sign-off-controls ctx required-role current-id]]]
      [:<>
       [h2 required-role-label]
       [:p.p-0.leading-tight
        (tr [:sign-off/no-parties-for-role])
        [:span.lowercase required-role-label]
        (tr [:sign-off/no-parties])]])))


(defmethod fr/render :sign-off/signature
  ;; Legacy single signature component only used by time-travelling form schemas
  [{:keys [tr sign-off-pdf] :as ctx} _ current-id _]
  [:div
   [sign-off-controls
    ctx
    ;; Legacy sign-off had only applicant sign-offs
    (role-model/get-role-with-key (<sub [::ws-subs/authority-group]) :role/applicant)
    current-id]
   (when sign-off-pdf
     [:div.pt-4
      [file-download/download-workspace-file-button sign-off-pdf
       {:text (tr [:sign-off/download-signed])}]])])


(defmethod fr/render :sign-off/declaration
  ;; Legacy sign-off component only used by time-travelling form schemas
  [{:keys [tr]} & _]
  (when-let [applicant (<sub [::principal-applicant])]
    [:p.mt-4 {}
     (tr [:sign-off/declaration]
         [(-> applicant :full-name)])]))


(defmethod fr/render :sign-off/declaration-section
  ;; Legacy sign-off component only used by time-travelling form schemas
  [ctx key-idx current-id [_ _ & children]]
  (let [required-role :role/applicant ; Legacy sign-off had only applicant sign-offs
        ctx           (assoc ctx :role required-role)]
    (for-frag-indexed [[i child] children]
      [fr/render-form-element ctx (str key-idx "-" i) current-id child])))


(defmethod fr/render :sign-off/signatures
  ;; New sign-off signature form component used whether there are multiple required signatures or not
  [{:keys [required-roles sign-off-pdf tr] :as ctx}
   _
   current-id
   [_ {:keys [class-name show-download-form? authorization-letter? upload-only?]}]]
  [:<>
   [:div {:class class-name}
    (for-frag [required-role required-roles]
      (if (<sub [::ws-subs/loading-config?])
        [:div (cc/skeleton-text {:paragraph true}) (cc/button-skeleton)]
        (sign-off (merge {:show-download-form?   show-download-form?
                          :authorization-letter? authorization-letter?
                          :upload-only?          upload-only?}
                         ctx)
                  required-role
                  current-id)))]
   (when sign-off-pdf
     [:div.mt-6
      [file-download/download-workspace-file-button sign-off-pdf
       {:text (tr [:sign-off/download-signed])}]])])


(defn button-for-next-action [tr size application-id disabled? {:keys [signal-id on-success]}]
  (cc/button
    {:id               (keyword (str signal-id "-button"))
     :data-cy          (tr [signal-id])
     :icon             i/arrow-right
     :icon-description (tr [signal-id])
     :on-click         #(>evt [:app.front.process/signal
                               application-id
                               [signal-id]
                               (when on-success
                                 {:on-success on-success})])
     :disabled         disabled?
     :size             (or size :small)
     :icon-position    :right}
    (tr [signal-id])))


(defn button-and-errors-for-next-action [{:keys [tr application-id size]} {:keys [signal-id] :as command}]
  (let [signal-status (<sub [::app-progress/signal-allowed-status signal-id])
        errors        (<sub [::app-progress/visible-errors-for-the-signal signal-id])
        disabled?     (not= signal-status :allowed)]
    ;; only show the button if the signal is not forbidden
    (when (contains? #{:allowed :disabled} signal-status)
      {:button    (button-for-next-action tr size application-id (not= signal-status :allowed) command)
       :disabled? disabled?
       :errors    (map #(tr [%]) errors)})))


(defn make-list-for-errors [tr errors]
  (when (seq errors)
    [:ul.mb-6
     (for [warning (distinct errors)]
       [:li {:key (str warning)}
        [:div.flex.items-center.mb-2 {:data-cy "sign-off-error"}
         (cc/icon {:icon        i/circle-attention
                   :description (tr [:error/error])
                   :fill        "#f71735"
                   :class-name  "mr-2 w-6 flex-shrink-0"})
         [:div.leading-tight.flex-shrink warning]]])]))


(defn actions [ctx]
  (->> [{:signal-id  :process.signal/submit
         :on-success [::back-to-workspace]}
        {:signal-id  :process.signal/to-pre-submit-payment
         :on-success [::continue-to-payment]}]
       (map (partial button-and-errors-for-next-action ctx))
       (remove nil?)
       ((juxt #(map :button %)
              #(mapcat :errors %)))))


(defn pending [tr signal]
  (cc/inline-loading
    {:description (str (tr [(ui/append-to-keyword-ns ".loading" signal) :processing])
                       "...")}))


(defn relevant-signal [signals]
  (-> signals
      (set/intersection #{:process.signal/submit
                          :process.signal/to-pre-submit-payment})
      first))


(defn- back-to-wizard-button [tr workspace-id]
  [ui/button
   {:kind :tertiary
    :size :default
    :href (href/href :view/application-wizard-workspace
                     {:workspace-id workspace-id
                      :step         "summary"})}
   (tr [:application-wizard/back-to-wizard])])

(defn- next-actions-buttons [tr workspace-id from-wizard? current-processing-signal buttons]
  (into [:div.flex.flex-row.gap-2.items-center]
        (concat
          (when from-wizard?
            [[back-to-wizard-button tr workspace-id]])
          (if current-processing-signal
            [[:div.ml-4 (pending tr current-processing-signal)]]
            buttons))))

(defn next-actions-section
  "Displays the next action/signal that user can do after sign off and
  list of errors (more like TODO list) related to the current sign off or to the next action.
  Main purpose for this section is to make sure that the user remembers to submit the application after sign off"
  [{:keys [tr required-roles form workspace-id] :as ctx}]
  (let [lang                         (<sub [:language])
        desktop?                     (<sub [::media-query/breakpoint-matches? :md])
        [buttons errors-for-actions] (actions (assoc ctx :size :default))
        role-key-has-not-signed-off? (->> required-roles
                                          (map :party-role/key)
                                          (sign-off-model/roles-that-have-not-signed-off form))
        errors-for-the-user          (->> required-roles
                                          (filter (comp role-key-has-not-signed-off? :party-role/key))
                                          (map #(tr [:sign-off/sign-off-by-the-role-is-required]
                                                    [(role-model/party-role-name % lang)]))
                                          (concat errors-for-actions)
                                          (make-list-for-errors tr))
        current-processing-signal    (-> (<sub [:app.front.process/current-processing-signals])
                                         relevant-signal)
        from-wizard?                 (<sub [::from-wizard?])]
    (if (<sub [::ws-subs/loading-config?])
      [section/section-skeleton]
      [:section
       [:div errors-for-the-user]
       (if desktop?
         [next-actions-buttons tr workspace-id from-wizard? current-processing-signal buttons]
         [:<>
          [:div {:class "h-20" :aria-hidden true} "\u00a0"]
          [:div {:class   (str "fixed bottom-0 left-0 right-0 z-40 p-4 flex flex-row items-center "
                               "justify-end bg-white border-t border-solid border-gray-400 shadow-lg")
                 :data-cy "sign-off-mobile-action-bar"}
           [next-actions-buttons tr workspace-id from-wizard? current-processing-signal buttons]]])])))



(defn needs-changes-modal [tr application-id]
  (let [{:keys [open? text]} (<sub [::needs-changes-modal])]
    [ui/confirm-modal
     {:id           "sign-off-needs-changes-modal"
      :tr           tr
      :open?        (boolean open?)
      :on-close     #(>evt [::hide-needs-changes-modal])
      :on-yes-click #(do
                       (>evt [:app.front.process/signal
                              application-id
                              [:process.signal/sign-off-needs-changes
                               {:text text}]])
                       (>evt [::hide-needs-changes-modal]))
      :modal-title  (tr [:sign-off/needs-changes-modal-title])
      :dangerous?   true}
     [:div.mb-3
      (tr [:sign-off/needs-changes-modal-desc])]
     [ui/text-area
      {:id          "sign-off-acceptance-modal-textarea"
       :label-text  (tr [:acceptance/request-text-field-title])
       :helper-text (tr [:acceptance/request-text-field-help])
       :value       (or text "")
       :on-change   #(>evt [::set-needs-changes-text (tval %)])
       :placeholder (tr [:acceptance/request-text-field-placeholder])}]]))


(defn view [match]
  (let [workspace-id   (-> match :parameters :path :workspace-id)
        workspace      (<sub [::entities/entity {:workspace/id workspace-id}])
        application    (workspace-model/workspace-application workspace)
        application-id (:db/id application)
        sof            (:application/sign-off-form application)
        tr             (<sub [::ws-subs/tr])
        required-roles (<sub [::ws-subs/required-so-roles])
        sign-off-pdf   (<sub [::sign-off-pdf-file])]
    [:<>
     [needs-changes-modal tr application-id]
     (if sof
       (let [schema      (:form/schema sof)
             data-schema (:form.schema/data-schema schema)
             ctx         (util/mmap {:target-id      application-id
                                     :user           (<sub [:session/user])
                                     :form           sof
                                     :form-id        (:db/id sof)
                                     :initial?       true
                                     :disabled?      (->> required-roles
                                                          (map :party-role/key)
                                                          (sign-off-model/signed-for-role-keys? sof))
                                     :tr-schema      (<sub [::form-subs/tr-schema tr])
                                     ::permit-type   (app-model/permit-type application)
                                     ::parties       (<sub [::processed-parties])
                                     ::information   (<sub [::information])
                                     ::properties    (<sub [::properties])
                                     ::get-principal (fn [role] (<sub [::principal-for-role role]))}
                                    workspace-id application-id tr sign-off-pdf required-roles)]
         (if data-schema
           [:div.cloudpermit-page.form-page
            [header ctx]
            (fr/render ctx 0 application-id data-schema)
            [next-actions-section ctx]]
           [not-found/not-found-view]))
       [not-found/not-found-view])]))

(def route
  {:name        :view/sign-off
   :view-sub    [::view-data]
   :view        view
   :parameters  {:query {(s/optional-key :wizard) s/Bool}}
   :controllers [{:parameters {:path [:workspace-id]}
                  :start      (fn [params]
                                (scroll-to-top)
                                (>evt [::application-data (-> params :path :workspace-id)]))}]})


(rf/reg-event-fx ::show-sign-off-view
  (fn [_ [_ process-target]]
    {:route/push {:route  :view/sign-off
                  :params {:workspace-id (-> process-target :entity/parent :workspace/id)}}}))
